import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  defaultPositions,
  getRolesPlusOrPlusPlus,
  playersSelector,
} from "@/redux/players/playersSlice";
import { useTranslation } from "@repo/shared/i18n";
import { comparePlayerKey } from "@repo/shared/redux";
import { DetailedPlayer } from "@repo/shared/types";
import { useSelector } from "react-redux";

import {
  CompareCompactRow,
  CompareGroupedBlock,
  ComparePinnedRowHeader,
  CompareRow,
  CompareSectionShell,
} from "./CompareSectionShell";
import { Mode } from "./ModeToggle";

type RoleOwnership = ReturnType<typeof getRolesPlusOrPlusPlus>;

const roleState = (player: DetailedPlayer, playerRoleId: number) =>
  getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus: player.rolesPlus ?? [],
  });

/** Grouped/compact role chip: shortName + plus icons, dimmed when unowned. */
const RoleChip = ({ name, state }: { name: string; state: RoleOwnership }) => (
  <div
    className={cn(
      "flex items-center gap-x-1 rounded bg-white/5 px-1.5 py-0.5",
      !state && "opacity-40",
    )}
  >
    <span
      className={cn(
        "text-[11px] font-medium",
        state ? "text-neutral-200" : "text-neutral-500",
      )}
    >
      {name}
    </span>
    <PlusPlus plus={state === "plus"} plusPlus={state === "plusPlus"} />
  </div>
);

/**
 * "Roles+" section, mirroring the PlayStyles section: one subsection per
 * position any compared player can play (in `defaultPositions` order), listing
 * every role tied to that position with the player's plus / plus-plus state
 * (`rolesPlus` carries role id for plus, id + 100 for plus-plus). List mode =
 * one role per row on the column axis; grouped mode = per-player flex-wrap of
 * role chips inside each position subsection; compact mode = one per-player
 * flex-wrap of only the roles that player owns, plus-plus first, no position
 * subsections. Same rule as PlayStyles: roles
 * nobody has a plus for are hidden, and a position with zero owned roles
 * disappears (a player without the plus still shows the role dimmed when
 * another player has it).
 */
export const RolesCompareSection = ({
  players,
  gridStyle,
  open,
  onToggle,
  mode,
  pinnedWidth,
}: {
  players: DetailedPlayer[];
  gridStyle: React.CSSProperties;
  open: boolean;
  onToggle: () => void;
  mode: Mode;
  /**
   * Mobile: sticky-left width for the section title and the position headers
   * inside the shared horizontal scroller.
   */
  pinnedWidth?: number;
}) => {
  const { playerRoles } = useSelector(playersSelector);
  const { positions } = useSelector(contentLabelsSelector);

  const { t } = useTranslation("compare");
  const sections = defaultPositions
    .map((p) => p.label)
    .filter((position) =>
      players.some((player) => player.positions?.includes(position)),
    )
    .map((position) => ({
      position,
      // Only roles at least one player owns (plus or plus-plus) — a fully
      // unowned role says nothing in a comparison.
      roles: playerRoles
        .filter((role) => role.position === position)
        .filter((role) => players.some((player) => roleState(player, role.id))),
    }))
    .filter(({ roles }) => roles.length > 0);

  return (
    <CompareSectionShell
      title={t("sections.rolesPlus")}
      count={players.length}
      gridStyle={gridStyle}
      open={open}
      onToggle={onToggle}
      mode={mode}
      pinnedWidth={pinnedWidth}
    >
      {mode === "list" ? (
        sections.map(({ position, roles }) => (
          <div key={position}>
            <ComparePinnedRowHeader
              label={positions[position]?.name ?? position}
              pinnedWidth={pinnedWidth}
            />
            {roles.map((role, rowIndex) => (
              <CompareRow key={role.id} index={rowIndex} gridStyle={gridStyle}>
                {players.map((player) => {
                  const state = roleState(player, role.id);
                  return (
                    <div
                      key={comparePlayerKey(player)}
                      className="flex items-center gap-[7px] pl-[30px]"
                    >
                      <span
                        className={cn(
                          "text-xs font-medium",
                          state ? "text-neutral-200" : "text-neutral-600",
                        )}
                      >
                        {role.name}
                      </span>
                      <PlusPlus
                        plus={state === "plus"}
                        plusPlus={state === "plusPlus"}
                      />
                    </div>
                  );
                })}
              </CompareRow>
            ))}
          </div>
        ))
      ) : mode === "compact" ? (
        <CompareCompactRow
          gridStyle={gridStyle}
          players={players}
          renderPlayer={(player) => {
            // Only roles the player owns, plus-plus before plus — one
            // dense wrap per player, no position grouping.
            const owned = sections
              .flatMap(({ roles }) => roles)
              .map((role) => ({ role, state: roleState(player, role.id) }))
              .filter(({ state }) => state)
              .sort(
                (a, b) =>
                  Number(b.state === "plusPlus") -
                  Number(a.state === "plusPlus"),
              );
            return owned.map(({ role, state }) => (
              <RoleChip
                key={role.id}
                name={role.shortName ?? role.name}
                state={state}
              />
            ));
          }}
        />
      ) : (
        sections.map(({ position, roles }, blockIndex) => (
          <CompareGroupedBlock
            key={position}
            label={positions[position]?.name ?? position}
            index={blockIndex}
            gridStyle={gridStyle}
            players={players}
            renderPlayer={(player) =>
              roles.map((role) => (
                <RoleChip
                  key={role.id}
                  name={role.shortName ?? role.name}
                  state={roleState(player, role.id)}
                />
              ))
            }
          />
        ))
      )}
    </CompareSectionShell>
  );
};
